<template>
  <EditorTitleOverlay data-testid="edit-scene-title">
    <ChevronLeftIcon class="w-5 h-5 mr-4 cursor-pointer" data-testid="back-btn" @click="back" />

    <div class="flex items-center justify-center flex-grow mx-auto text-xl drop-shadow max-w-3/4">
      <div class="flex items-start justify-center text-xl text-gray-200 cursor-pointer slide-title max-w-4/5">
        <span role="button" :title="showSlideTitle ? 'Title is visible when presenting' : 'Title is hidden when presenting'">
          <EyeIcon v-if="showSlideTitle" class="w-5 h-5 mx-2 mt-2 -top-px" @click="$emit('update:show-slide-title')" />
          <EyeSlashIcon v-else class="w-5 h-5 mx-2 mt-2 -top-px" @click="$emit('update:show-slide-title')" />
        </span>

        <InlineEditable
          :key="(isComposingNewScene && progress === 1).toString()"
          v-model:value="title as string"
          data-testid="scene-title-input"
          :disabled="loading"
          :maxlength="255"
          :minwidth="!title ? '14.5rem' : 'initial'"
          :focus-on-mount="isComposingNewScene && progress === 1"
          placeholder="Insert Slide Name Here"
          class="max-w-full whitespace-nowrap"
          theme="dark"
          @clear="setTitle(slideTitle)"
          @cancel="setTitle(slideTitle)"
        />
      </div>
    </div>

    <div class="flex items-center justify-end">
      <VButton
        class="mr-4"
        size="sm"
        variant="light"
        data-testid="save-btn"
        :disabled="disabled || loading || leaving"
        data-intercom-target="3d-save-button"
        @click="save"
        >{{ loading && !leaving && !isSavingAsScene ? 'Saving...' : 'Save' }}</VButton
      >
      <VButton
        v-if="!isComposingNewScene"
        size="sm"
        variant="light"
        :disabled="disabled || loading || leaving"
        data-testid="save-as-btn"
        @click="saveAs"
        >{{ isSavingAsScene ? 'Creating...' : 'Save As' }}</VButton
      >
      <VButton v-else size="sm" variant="light" data-testid="cancel-btn" :disabled="loading || leaving" @click="cancel">Cancel</VButton>
    </div>
  </EditorTitleOverlay>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { watch, ref, onMounted } from 'vue'
import { ChevronLeftIcon } from '@heroicons/vue/24/outline'
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/solid'

const props = defineProps({
  disabled: {
    type: Boolean,
    required: true,
  },
  progress: {
    type: Number,
    required: true,
  },
  isSavingAsScene: {
    type: Boolean,
    required: true,
  },
  slideTitle: {
    type: String as PropType<string | null>,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  isComposingNewScene: {
    type: Boolean,
    default: false,
  },
  showSlideTitle: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:scene-title', 'update:show-slide-title', 'save', 'save-as', 'back', 'cancel'])

const title = ref(props.slideTitle)
const leaving = ref(false)

watch(
  () => props.slideTitle,
  slideTitle => {
    title.value = slideTitle
  },
  { immediate: true }
)

watch(
  () => props.isSavingAsScene,
  (newIsSavingAsScene, oldIsSavingAsScene) => {
    if (oldIsSavingAsScene && !newIsSavingAsScene) leaving.value = false
  }
)

onMounted(() => {
  if (props.isComposingNewScene) {
    title.value = ''
  }
})

function back() {
  leaving.value = true
  emit('back')
}

function save() {
  leaving.value = true
  emit('save', title.value?.trim() || props.slideTitle)
}

function saveAs() {
  leaving.value = true
  emit('save-as', title.value?.trim() || props.slideTitle)
}

function setTitle(newTitle: string | null) {
  if (!newTitle) return
  title.value = newTitle
}

function cancel() {
  leaving.value = true
  emit('cancel')
}
</script>

<style lang="stylus" scoped>
svg
  min-width: 1.25rem

.slide-title
  &.hidden
    @apply text-gray-500

  &:hover
    @apply text-white
</style>
